// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/components/HRComponents/JobOpeningDetail.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import { 
  Briefcase, MapPin, DollarSign, Users, Plus, RefreshCw, Edit2, 
  Globe, Lock, Calendar, ClipboardList, User, 
  Clock
} from 'lucide-react'; 
import { toast } from 'react-toastify';
import SummaryApi from '../../common/SummaryApi';
import api from '../../common/apiClient';
import ModernTable from '../ui/ModernTable';
import CandidateForm from './CandidateForm';
import MoveStageDrawer from './MoveStageDrawer';
import { formatDateTime } from "../../helpers/formatDateTime";

const JobOpeningDetail = ({ jobId, onClose, onEdit }) => {
  const id = jobId;

  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Controls the "Add Candidate" drawer (mode="apply")
  const [isCandidateDrawerOpen, setIsCandidateDrawerOpen] = useState(false);

  // Controls the "Edit Candidate" drawer (mode="edit")
  const [candidateToEdit, setCandidateToEdit] = useState(null); 

  // Update candidate stage form inside Offcanvas
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  const fetchJobAndCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const [jobRes, candidateRes] = await Promise.all([
        api.get(SummaryApi.getRecruitmentJobById.url(id)),
        api.get(`${SummaryApi.getAllCandidates.url}?jobOpeningId=${id}`)
      ]);
      
      if (jobRes.data.success) {
        setJob(jobRes.data.data || jobRes.data.job);
      }
      if (candidateRes.data.success) {
        setCandidates(candidateRes.data.data || []);
      }
    } catch (err) {
      toast.error('Failed to load detail page records');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchJobAndCandidates();
  }, [fetchJobAndCandidates]);

  // Clean up escape quotes from skills array strings (e.g., \"Flutter\" -> Flutter)
  const cleanSkillString = (skillStr) => {
    if (!skillStr) return '';
    return skillStr.replace(/"/g, '');
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700 border-gray-200',
      published: 'bg-green-100 text-green-700 border-green-200',
      on_hold: 'bg-amber-100 text-amber-700 border-amber-200',
      closed: 'bg-red-100 text-red-700 border-red-200',
    };
    return (
      <span className={`px-2.5 py-1 text-[10px] font-bold uppercase rounded-full border ${styles[status] || styles.draft}`}>
        {status?.replace('_', ' ')}
      </span>
    );
  };

  const candidateColumns = [
    {
      label: 'Candidate Name',
      key: 'name',
      render: (row) => (
        <span className="font-semibold text-gray-900">{row.firstName} {row.lastName}</span>
      )
    },
    {
      label: 'Email / Phone',
      key: 'email',
      render: (row) => (
        <div className="text-xs">
          <div>{row.email}</div>
          <div className="text-gray-400 mt-0.5">{row.mobile || 'No Mobile'}</div>
        </div>
      )
    },
    {
      label: 'Experience',
      key: 'totalExperienceYears', 
      render: (row) => <span className="text-xs">{row.totalExperienceYears || 0} Years</span>
    },
    {
      label: 'Pipeline Stage',
      key: 'currentStage',
      render: (row) => {
        const stageStyles = {
          applied: 'bg-blue-100 text-blue-700 border-blue-200',
          resume_screening: 'bg-purple-100 text-purple-700 border-purple-200',
          shortlisted: 'bg-pink-100 text-pink-700 border-pink-200',
          interview_scheduled: 'bg-indigo-100 text-indigo-700 border-indigo-200',
          interview_completed: 'bg-orange-100 text-orange-700 border-orange-200',
          selected: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          offered: 'bg-amber-100 text-amber-700 border-amber-200',
          onboarding: 'bg-cyan-100 text-cyan-700 border-cyan-200',
          hired: 'bg-green-100 text-green-700 border-green-200',
          rejected: 'bg-red-100 text-red-700 border-red-200',
          on_hold: 'bg-gray-100 text-gray-700 border-gray-200',
        };
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`px-2.5 py-0.5 rounded-full border text-[10px] font-bold uppercase ${stageStyles[row.currentStage] || 'bg-gray-100 text-gray-700'}`}>
              {row.currentStage?.replace('_', ' ')}
            </span>
            {row.isOnHold && (
              <span className="px-2.5 py-0.5 rounded-full border border-amber-200 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase animate-pulse">
                On Hold
              </span>
            )}
          </div>
        );
      }
    }
  ];

  const candidateActions = [
    {
      label: 'Edit Profile Info',
      icon: <Edit2 className="h-4 w-4" />,
      onClick: (row) => {
        setCandidateToEdit(row); 
      }
    },
    {
      label: 'Change Stage',
      icon: <RefreshCw className="h-4 w-4" />,
      onClick: (row) => {
        setSelectedCandidate(row);
      }
    }
  ];

  if (loading || !job) {
    return <div className="p-8 text-center font-semibold text-gray-500">Loading details...</div>;
  }

  return (
    <>
    <div className="space-y-6">
      
      {/* Header Panel (Responsive Flex Layout with Dynamic Badges) */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-5 rounded-xl border shadow-sm">
        <div className="flex items-start sm:items-center gap-3">
          <button 
            onClick={onClose} 
            type="button"
            className="rounded-lg bg-red-50 border border-red-100 p-2.5 text-textPrimary hover:text-primary transition-colors shrink-0 mt-1 sm:mt-0"
          >
            <FaArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">{job.title}</h1>
              <div className="flex items-center gap-1.5 flex-wrap">
                {/* Status Badge */}
                {getStatusBadge(job.status)}
                
                {/* Visibility Badge */}
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-bold uppercase border ${
                  job.isPublic 
                    ? 'bg-blue-50 text-blue-700 border-blue-100' 
                    : 'bg-purple-50 text-purple-700 border-purple-100'
                }`}>
                  {job.isPublic ? <Globe className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                  {job.isPublic ? 'Public Post' : 'Internal Only'}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-1.5">Code: <span className="font-semibold text-gray-700">{job.jobId}</span> &bull; {job.departmentId?.name || 'Unassigned Department'}</p>
          </div>
        </div>

        {/* Responsive Header Actions Panel */}
        <div className="flex gap-2 w-full lg:w-auto shrink-0">
          <button 
            onClick={() => onEdit(job)} 
            className="flex-1 lg:flex-none flex items-center justify-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-xs font-bold transition-all"
          >
            <Edit2 className="h-4 w-4" /> Edit
          </button>
          <button 
            onClick={() => setIsCandidateDrawerOpen(true)} 
            className="flex-1 lg:flex-none flex items-center justify-center gap-2 bg-primary hover:bg-secondary text-white px-4 py-2 rounded-lg transition-all text-xs font-bold"
          >
            <Plus className="h-4 w-4" /> Add Candidate
          </button>
        </div>
      </div>

      {/* Stats Parameters Row (Fully Formatted) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border rounded-xl p-4 flex items-center gap-3 shadow-sm">
          <div className="p-3 rounded-lg bg-red-50 text-primary"><Briefcase className="h-5 w-5" /></div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase">Mode & Type</p>
            <p className="font-bold text-gray-800 capitalize text-xs mt-0.5">{job.workMode} &bull; {job.employmentType?.replace('_', ' ')}</p>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4 flex items-center gap-3 shadow-sm">
          <div className="p-3 rounded-lg bg-red-50 text-primary"><MapPin className="h-5 w-5" /></div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase">Work Location</p>
            <p className="font-bold text-gray-800 text-xs mt-0.5">{job.location || 'N/A'}</p>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4 flex items-center gap-3 shadow-sm">
          <div className="p-3 rounded-lg bg-red-50 text-primary"><DollarSign className="h-5 w-5" /></div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase">Salary Package Bounds</p>
            <p className="font-bold text-gray-800 text-xs mt-0.5">
              {job.salaryRangeMin ? `₹ ${job.salaryRangeMin.toLocaleString('en-IN')} - ₹ ${job.salaryRangeMax.toLocaleString('en-IN')}` : 'Undisclosed'}
            </p>
          </div>
        </div>
        <div className="bg-white border rounded-xl p-4 flex items-center gap-3 shadow-sm">
          <div className="p-3 rounded-lg bg-red-50 text-primary"><Users className="h-5 w-5" /></div>
          <div>
            <p className="text-[10px] text-gray-400 font-bold uppercase">Hiring Targets</p>
            <p className="font-bold text-gray-800 text-xs mt-0.5">{job.numberOfHires} / {job.numberOfOpenings} Vacancies Filled</p>
          </div>
        </div>
      </div>

      {/* Main Content Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Specifications Column */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white border rounded-xl p-5 shadow-sm space-y-5 h-fit">
            <h2 className="font-bold text-gray-900 border-b pb-2 text-xs uppercase tracking-wider">Specifications Mapping</h2>
            
            {/* Organizational Details */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <span className="text-[9px] font-bold text-gray-400 uppercase">Division</span>
                <p className="font-semibold text-gray-700 mt-1">{job.divisionId?.name || 'N/A'}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                <span className="text-[9px] font-bold text-gray-400 uppercase">Department</span>
                <p className="font-semibold text-gray-700 mt-1">{job.departmentId?.name || 'N/A'}</p>
              </div>
            </div>

            {/* Target Experience Bounds */}
            <div className="bg-gray-50 p-3.5 rounded-lg border border-gray-100 text-xs">
              <span className="text-[9px] font-bold text-gray-400 uppercase">Experience Bounds Required</span>
              <p className="font-semibold text-gray-700 mt-1">
                {job.experienceMin} to {job.experienceMax || 'N/A'} Years Experience
              </p>
            </div>

            {/* Cleaned Skills Badge Chips */}
            <div className="space-y-2">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Target Skills</span>
              {job.skills?.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {job.skills.map((skill, idx) => (
                    <span key={idx} className="bg-red-50 text-primary border border-red-100 px-2.5 py-0.5 rounded text-[10px] font-semibold">
                      {cleanSkillString(skill)}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No skills configured</p>
              )}
            </div>

            {/* Creator / Date Sourcing details */}
            <div className="border-t pt-4 space-y-3 text-xs text-gray-500">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-gray-400" />
                <span>Posted By: <span className="font-semibold text-gray-700">{job.createdBy ? `${job.createdBy.firstName} ${job.createdBy.lastName}` : 'System'}</span></span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span>Created: {formatDateTime(job.createdAt, false)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Clock className="h-4 w-4" />
                <span>Last Updated: {formatDateTime(job.updatedAt, false)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Details Column (Detailed Description + Application Pipelines) */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Detailed Job Roles description card */}
          <div className="bg-white border rounded-xl p-5 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b pb-2">
              <ClipboardList className="h-4.5 w-4.5 text-gray-400" />
              <h2 className="font-bold text-gray-900 text-xs uppercase tracking-wider">Work Profile Description</h2>
            </div>
            
            {job.description && (
              <div className="space-y-1.5">
                <p className="text-[10px] text-gray-400 font-bold uppercase">DESCRIPTION SUMMARY</p>
                <p className="text-xs text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-lg border border-gray-100">{job.description}</p>
              </div>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {job.requirements?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] text-gray-400 font-bold uppercase">REQUIREMENTS & SPECS</p>
                  <ul className="list-disc pl-4 space-y-1.5">
                    {job.requirements.map((req, i) => (
                      <li key={i} className="text-xs text-gray-600 leading-relaxed font-medium">{req}</li>
                    ))}
                  </ul>
                </div>
              )}
              {job.responsibilities?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] text-gray-400 font-bold uppercase">KEY RESPONSIBILITIES</p>
                  <ul className="list-disc pl-4 space-y-1.5">
                    {job.responsibilities.map((resp, i) => (
                      <li key={i} className="text-xs text-gray-600 leading-relaxed font-medium">{resp}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Sourced Applicants pipeline list */}
          <div className="bg-white border rounded-xl p-5 shadow-sm space-y-4">
            <div className="flex justify-between items-center border-b pb-2">
              <h2 className="font-bold text-gray-900 text-xs uppercase tracking-wider">Application Pipeline</h2>
              <span className="text-xs font-semibold text-gray-400">{candidates.length} Registered Applicants</span>
            </div>

            <ModernTable
              columns={candidateColumns}
              data={candidates}
              actions={candidateActions}
              selectable={false}
            />
          </div>

        </div>

      </div>
    </div>

    {/* CandidateForm Component in "apply" mode */}
    <CandidateForm 
      isOpen={isCandidateDrawerOpen}
      onClose={() => setIsCandidateDrawerOpen(false)}
      mode="apply"
      jobOpeningId={id}
      organizationId={job.organizationId}
      onSave={fetchJobAndCandidates} 
    />

    {/* CandidateForm Component in "edit" mode */}
    <CandidateForm 
      isOpen={!!candidateToEdit}
      onClose={() => setCandidateToEdit(null)}
      mode="edit"
      candidate={candidateToEdit}
      onSave={fetchJobAndCandidates} 
    />

    {/* Reusable MoveStageDrawer Component */}
    <MoveStageDrawer 
      isOpen={!!selectedCandidate}
      onClose={() => setSelectedCandidate(null)}
      candidate={selectedCandidate}
      onSave={fetchJobAndCandidates} 
    />
    </>
  );
};

export default JobOpeningDetail;